export type ReasoningStepStatus = 'running' | 'done' | 'error';

export type ReasoningStep = {
  id: string;
  title: string;
  description?: string;
  /** Live page-gen reasoning streamed while this step runs; rendered as a muted scrollable bubble. */
  thinking?: string;
  status: ReasoningStepStatus;
  kind: 'action';
};

export type ReasoningTrace = {
  steps: ReasoningStep[];
  startedAt: number;
  endedAt?: number;
};

export function emptyTrace(startedAt: number): ReasoningTrace {
  return { steps: [], startedAt };
}

const mapStep = (
  t: ReasoningTrace,
  id: string,
  fn: (s: ReasoningStep) => ReasoningStep
): ReasoningTrace => ({
  ...t,
  steps: t.steps.map((s) => (s.id === id ? fn(s) : s)),
});

export function startStep(t: ReasoningTrace, id: string): ReasoningTrace {
  if (t.steps.some((s) => s.id === id)) return t;
  return {
    ...t,
    steps: [
      ...t.steps,
      { id, title: 'Working', status: 'running', kind: 'action' },
    ],
  };
}

export function titleStep(
  t: ReasoningTrace,
  id: string,
  title: string
): ReasoningTrace {
  return mapStep(t, id, (s) => ({ ...s, title }));
}

export function describeStep(
  t: ReasoningTrace,
  id: string,
  description: string
): ReasoningTrace {
  return mapStep(t, id, (s) => ({ ...s, description }));
}

export function setStepThinking(
  t: ReasoningTrace,
  id: string,
  thinking: string
): ReasoningTrace {
  return mapStep(t, id, (s) => ({ ...s, thinking }));
}

export function finishStep(
  t: ReasoningTrace,
  id: string,
  status: ReasoningStepStatus
): ReasoningTrace {
  return mapStep(t, id, (s) => ({ ...s, status }));
}

export function endTrace(t: ReasoningTrace, endedAt: number): ReasoningTrace {
  return { ...t, endedAt };
}

/**
 * Reopen an ENDED trace so a follow-up phase of the same user action can add
 * its step to the same working block (e.g. the resolve-first build's
 * "Reading the page data" joining the click's build trace after landing).
 * The final `endTrace` then stamps a duration spanning the whole action.
 * Reopen-on-arrival (rather than keeping the trace open across the gap) means
 * a follow-up that never happens leaves a properly ended trace, never a
 * forever-"Working…" block.
 */
export function reopenTrace(t: ReasoningTrace): ReasoningTrace {
  const { endedAt: _endedAt, ...rest } = t;
  return rest;
}
